import * as THREE from 'three';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/**
 * GSAP ScrollTrigger integration for the homepage diorama.
 * Maps scroll position (0→1) to:
 *   Section 1 (0–0.25): Close-up on 2 Hatchlings
 *   Section 2 (0.25–0.5): Pull back, 2 more pop in
 *   Section 3 (0.5–0.75): Slight rotation, deliverable bubbles
 *   Section 4 (0.75–1.0): Dramatic lower angle
 */

// Module-level scroll progress — read by the frame loop
let scrollProgress = 0;

// Camera keyframes for each section boundary
const CAMERA_KEYFRAMES = [
  // Section 1 start: close on two desks
  { pos: new THREE.Vector3(5, 5, 5), lookAt: new THREE.Vector3(0.1, 0.6, 0.2), zoom: 1.15 },
  // Section 2 start: pull back to show all
  { pos: new THREE.Vector3(8, 8, 8), lookAt: new THREE.Vector3(0, 0.5, 0.5), zoom: 1.0 },
  // Section 3 start: slight orbit
  { pos: new THREE.Vector3(9, 7, 7), lookAt: new THREE.Vector3(0, 0.8, 0.5), zoom: 1.0 },
  // Section 4 start: dramatic lower angle
  { pos: new THREE.Vector3(7, 4, 9), lookAt: new THREE.Vector3(0, 0.5, 0.5), zoom: 1.1 },
];

let scrollTriggerInstance: ScrollTrigger | null = null;

/**
 * Smooth interpolation (Hermite)
 */
function smoothstep(t: number): number {
  return t * t * (3 - 2 * t);
}

/**
 * Ease-out back (overshoot bounce for pop-in)
 */
function easeOutBack(t: number): number {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
}

/**
 * Set up ScrollTrigger to drive camera + character + deliverable animations.
 */
export function setupScrollAnimations(
  camera: THREE.OrthographicCamera,
  deskStations: THREE.Group[],
  deliverables: THREE.Group[],
): void {
  // Kill any existing instance
  killScrollAnimations();

  const overlay = document.getElementById('homepage-overlay');
  if (!overlay) return;

  // Temp vectors for interpolation
  const tempPos = new THREE.Vector3();
  const tempLookAt = new THREE.Vector3();

  scrollTriggerInstance = ScrollTrigger.create({
    trigger: overlay,
    start: 'top top',
    end: 'bottom bottom',
    scrub: 0.5,
    onUpdate: (self) => {
      scrollProgress = self.progress;
      const p = self.progress;

      // ── Camera interpolation ────────────────────────────
      const totalSegments = CAMERA_KEYFRAMES.length - 1;
      const rawSegment = p * totalSegments;
      const segment = Math.min(Math.floor(rawSegment), totalSegments - 1);
      const segmentT = smoothstep(rawSegment - segment);

      const kfA = CAMERA_KEYFRAMES[segment];
      const kfB = CAMERA_KEYFRAMES[Math.min(segment + 1, totalSegments)];

      tempPos.lerpVectors(kfA.pos, kfB.pos, segmentT);
      tempLookAt.lerpVectors(kfA.lookAt, kfB.lookAt, segmentT);

      camera.position.copy(tempPos);
      camera.lookAt(tempLookAt);
      camera.zoom = kfA.zoom + (kfB.zoom - kfA.zoom) * segmentT;
      camera.updateProjectionMatrix();

      // ── Desk stations pop-in (section 2) ────────────────
      // Stations 0 and 1 are visible from the start; 2+ pop in on scroll
      for (let i = 2; i < deskStations.length; i++) {
        const station = deskStations[i];
        const appearAt = 0.2 + (i - 2) * 0.06; // staggered
        const t = Math.max(0, Math.min(1, (p - appearAt) / 0.08));
        station.visible = t > 0;
        const scale = t > 0 ? easeOutBack(t) : 0;
        station.scale.setScalar(Math.max(0, scale));
      }

      // ── Deliverable bubbles (section 3) ─────────────────
      for (let i = 0; i < deliverables.length; i++) {
        const bubble = deliverables[i];
        const appearAt = 0.48 + i * 0.05;
        const t = Math.max(0, Math.min(1, (p - appearAt) / 0.08));
        bubble.visible = t > 0;
        const scale = t > 0 ? easeOutBack(t) * 0.8 : 0;
        bubble.scale.setScalar(Math.max(0, scale));

        // Fade material opacity
        if (t > 0) {
          bubble.traverse((child) => {
            if (child instanceof THREE.Mesh && child.material instanceof THREE.MeshStandardMaterial) {
              if (child.material.transparent) {
                child.material.opacity = child.material.userData?.baseOpacity
                  ? child.material.userData.baseOpacity * t
                  : t * 0.9;
              }
            }
          });
        }
      }
    },
  });
}

/**
 * Get current scroll progress (0 to 1).
 */
export function getScrollProgress(): number {
  return scrollProgress;
}

/**
 * Kill ScrollTrigger and reset state.
 */
export function killScrollAnimations(): void {
  if (scrollTriggerInstance) {
    scrollTriggerInstance.kill();
    scrollTriggerInstance = null;
  }
  scrollProgress = 0;

  // Kill all ScrollTrigger instances (safety cleanup)
  ScrollTrigger.getAll().forEach((st) => st.kill());
}
